/**
 * ScheduledMessagesPanel — Lista messaggi programmati con possibilità di cancellazione
 */

import { useState, useEffect } from 'react';
import { Clock, X, Loader2, Calendar } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useWhatsAppChatStore } from '@/lib/stores/whatsappChatStore';
import { tip } from '@/components/messages/ChatTooltip';

interface ScheduledMsg {
  id: string;
  chat_jid: string;
  content: string;
  scheduled_at: string;
  status: string;
}

export function ScheduledMessagesPanel() {
  const [messages, setMessages] = useState<ScheduledMsg[]>([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState<string | null>(null);
  const { wahaConfig } = useWhatsAppChatStore();

  const fetchMessages = async () => {
    if (!wahaConfig?.organizationId) return;
    setLoading(true);
    const { data } = await supabase
      .from('whatsapp_scheduled_messages')
      .select('id, chat_jid, content, scheduled_at, status')
      .eq('organization_id', wahaConfig.organizationId)
      .eq('status', 'scheduled')
      .order('scheduled_at', { ascending: true });
    setMessages(data || []);
    setLoading(false);
  };

  useEffect(() => { fetchMessages(); }, [wahaConfig?.organizationId]);

  const handleCancel = async (id: string) => {
    setCancelling(id);
    // Solo status='cancelled' (valido nel CHECK). Le colonne cancelled_at/cancelled_by
    // non esistono sulla tabella: includerle faceva fallire l'update silenziosamente,
    // lasciando il messaggio 'scheduled' → veniva comunque inviato dal cron core.
    const { error } = await supabase
      .from('whatsapp_scheduled_messages')
      .update({ status: 'cancelled' })
      .eq('id', id);
    if (error) {
      // Non rimuovere dalla lista: l'annullamento non è andato a buon fine
      console.error('[ScheduledMessagesPanel] cancel failed:', error.message);
      alert("Impossibile annullare il messaggio programmato. Riprova.");
      setCancelling(null);
      return;
    }
    setMessages((prev) => prev.filter((m) => m.id !== id));
    setCancelling(null);
  };

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' }) + ' ' +
      d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  };

  const formatJid = (jid: string) => jid.split('@')[0].replace(/\D/g, '').slice(-10);

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[rgba(55,53,47,0.09)] dark:border-white/10">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-[#787774]" />
          <span className="text-sm font-semibold text-[#37352F] dark:text-white">Programmati</span>
          {messages.length > 0 && (
            <span className="text-xs bg-[#2383E2] text-white rounded-full px-1.5 py-0.5 font-medium">{messages.length}</span>
          )}
        </div>
        <button onClick={fetchMessages} className="text-xs text-[#2383E2] hover:underline">Aggiorna</button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading && (
          <div className="flex justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-[#787774]" />
          </div>
        )}
        {!loading && messages.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center px-6">
            <Clock className="w-10 h-10 text-[#787774] mb-3 opacity-50" />
            <p className="text-sm text-[#37352F] dark:text-white font-medium">Nessun messaggio programmato</p>
            <p className="text-xs text-[#787774] mt-1">I messaggi schedulati appariranno qui</p>
          </div>
        )}
        {messages.map((msg) => (
          <div key={msg.id} className="px-4 py-3 border-b border-[rgba(55,53,47,0.06)] dark:border-white/5 hover:bg-[rgba(55,53,47,0.02)] dark:hover:bg-white/3 group">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 mb-1">
                  <Clock className="w-3 h-3 text-[#2383E2] flex-shrink-0" />
                  <span className="text-[11px] font-semibold text-[#2383E2]">{formatDate(msg.scheduled_at)}</span>
                </div>
                <p className="text-xs text-[#787774] mb-1">a: +{formatJid(msg.chat_jid)}</p>
                <p className="text-xs text-[#37352F] dark:text-white line-clamp-2">{msg.content}</p>
              </div>
              <button
                onClick={() => handleCancel(msg.id)}
                disabled={cancelling === msg.id}
                className="flex-shrink-0 p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-[#787774] hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                {...tip('Annulla')}
              >
                {cancelling === msg.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
